import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, Save, Eye, Send, FileText, Copy } from "lucide-react";
import { Link } from "react-router-dom";
import { Budget, BudgetStatus } from "@/types/budget";

interface BudgetFormHeaderProps {
  budget?: Budget;
  isEditing: boolean;
  isSubmitting: boolean;
  onSave: () => void;
  onSaveAndSend?: () => void;
  onPreview?: () => void;
  onDuplicate?: () => void;
  onSaveAsTemplate?: () => void;
  className?: string;
}

const statusColors: Record<BudgetStatus, string> = {
  draft: "bg-muted text-muted-foreground hover:bg-border",
  pending: "bg-amber-500/10 text-amber-700 dark:text-amber-300 hover:bg-amber-500/20",
  approved: "bg-green-500/10 text-green-700 dark:text-green-300 hover:bg-green-500/20",
  rejected: "bg-red-500/10 text-red-700 dark:text-red-300 hover:bg-red-500/20",
  expired: "bg-orange-500/10 text-orange-700 dark:text-orange-300 hover:bg-orange-500/20",
  converted: "bg-blue-500/10 text-blue-700 dark:text-blue-300 hover:bg-blue-500/20",
};

const statusLabels: Record<BudgetStatus, string> = {
  draft: "Rascunho",
  pending: "Pendente",
  approved: "Aprovado",
  rejected: "Rejeitado",
  expired: "Expirado",
  converted: "Convertido",
};

export function BudgetFormHeader({
  budget,
  isEditing,
  isSubmitting,
  onSave,
  onSaveAndSend,
  onPreview,
  onDuplicate,
  onSaveAsTemplate,
  className = "",
}: BudgetFormHeaderProps) {
  const getTitle = () => {
    if (isEditing && budget) {
      return `Editar Orçamento #${budget.number}`;
    }
    return "Novo Orçamento";
  };

  const getDescription = () => {
    if (isEditing && budget) {
      return `Cliente: ${budget.client_name || "Carregando..."}`;
    }
    return "Crie um novo orçamento de serviços para seus clientes";
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Navigation and Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/services/budgets">
            <Button variant="outline" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Voltar
            </Button>
          </Link>

          {budget && (
            <Badge
              variant="secondary"
              className={statusColors[budget.status]}
            >
              {statusLabels[budget.status]}
            </Badge>
          )}
        </div>

        <div className="flex items-center gap-2">
          {isEditing && onPreview && (
            <Button variant="outline" size="sm" onClick={onPreview}>
              <Eye className="h-4 w-4 mr-2" />
              Visualizar
            </Button>
          )}

          {isEditing && onDuplicate && (
            <Button variant="outline" size="sm" onClick={onDuplicate}>
              <Copy className="h-4 w-4 mr-2" />
              Duplicar
            </Button>
          )}

          {onSaveAsTemplate && (
            <Button variant="outline" size="sm" onClick={onSaveAsTemplate}>
              <FileText className="h-4 w-4 mr-2" />
              Salvar Modelo
            </Button>
          )}

          {isEditing && onSaveAndSend && (
            <Button
              variant="default"
              size="sm"
              onClick={onSaveAndSend}
              disabled={isSubmitting}
            >
              <Send className="h-4 w-4 mr-2" />
              {isSubmitting ? "Enviando..." : "Salvar e Enviar"}
            </Button>
          )}

          <Button
            variant="default"
            size="sm"
            onClick={onSave}
            disabled={isSubmitting}
          >
            <Save className="h-4 w-4 mr-2" />
            {isSubmitting ? "Salvando..." : isEditing ? "Salvar Alterações" : "Criar Orçamento"}
          </Button>
        </div>
      </div>

      {/* Title and Description */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">
          {getTitle()}
        </h1>
        <p className="text-muted-foreground mt-1">
          {getDescription()}
        </p>
      </div>

      {/* Budget Info */}
      {budget && (
        <div className="flex items-center gap-6 text-sm text-muted-foreground">
          <div>
            <span className="font-medium">Valor Total:</span>{" "}
            {new Intl.NumberFormat("pt-BR", {
              style: "currency",
              currency: "BRL",
            }).format(budget.final_value)}
          </div>

          <div>
            <span className="font-medium">Itens:</span>{" "}
            {budget.items_count || 0}
          </div>

          <div>
            <span className="font-medium">Criado em:</span>{" "}
            {new Date(budget.created_at).toLocaleDateString("pt-BR")}
          </div>

          {budget.responsible_user_name && (
            <div>
              <span className="font-medium">Responsável:</span>{" "}
              {budget.responsible_user_name}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Loading skeleton for header
export function BudgetFormHeaderSkeleton() {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Skeleton className="h-9 w-24" />
          <Skeleton className="h-6 w-20" />
        </div>
        <div className="flex items-center gap-2">
          <Skeleton className="h-9 w-24" />
          <Skeleton className="h-9 w-32" />
        </div>
      </div>

      <div>
        <Skeleton className="h-9 w-64 mb-2" />
        <Skeleton className="h-5 w-96" />
      </div>
    </div>
  );
}